import React, { useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

type ViewerImage = {
  url: string;
  originalName?: string;
};

interface FullScreenImageViewerProps {
  images: ViewerImage[];
  open: boolean;
  selectedIndex: number;
  title?: string;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

const FullScreenImageViewer: React.FC<FullScreenImageViewerProps> = ({
  images,
  open,
  selectedIndex,
  title,
  onClose,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const hasImages = images.length > 0;
  const hasMultipleImages = images.length > 1;
  const normalizedIndex = hasImages ? ((selectedIndex % images.length) + images.length) % images.length : 0;
  const currentImage = hasImages ? images[normalizedIndex] : null;

  const handlePrevious = useCallback(() => {
    if (!hasMultipleImages) {
      return;
    }

    onNavigate((normalizedIndex - 1 + images.length) % images.length);
  }, [hasMultipleImages, images.length, normalizedIndex, onNavigate]);

  const handleNext = useCallback(() => {
    if (!hasMultipleImages) {
      return;
    }

    onNavigate((normalizedIndex + 1) % images.length);
  }, [hasMultipleImages, images.length, normalizedIndex, onNavigate]);

  useEffect(() => {
    if (!open || !hasMultipleImages) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePrevious();
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, hasMultipleImages, handleNext, handlePrevious]);

  return (
    <Dialog
      fullScreen
      open={open && hasImages}
      onClose={onClose}
      PaperProps={{
        sx: {
          bgcolor: 'rgba(14, 14, 14, 0.98)',
          backgroundImage: 'none',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          height: '100dvh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, sm: 8 },
          py: { xs: 8, sm: 10 },
        }}
      >
        <IconButton
          aria-label={t('common.actions.close')}
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: 'common.white',
            bgcolor: 'rgba(0, 0, 0, 0.35)',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.55)' },
          }}
        >
          <Close />
        </IconButton>

        {title && (
          <Typography
            variant="subtitle1"
            sx={{
              position: 'absolute',
              top: 20,
              left: 20,
              right: 72,
              color: 'common.white',
            }}
          >
            {title}
          </Typography>
        )}

        {hasMultipleImages && (
          <IconButton
            aria-label={t('common.actions.previous')}
            onClick={handlePrevious}
            sx={{
              position: 'absolute',
              left: { xs: 12, sm: 24 },
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'common.white',
              bgcolor: 'rgba(0, 0, 0, 0.35)',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.55)' },
            }}
          >
            <ChevronLeft />
          </IconButton>
        )}

        {currentImage && (
          <Box
            component="img"
            src={currentImage.url}
            alt={currentImage.originalName || title || t('common.labels.image')}
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              borderRadius: 2,
              boxShadow: '0 24px 80px rgba(0, 0, 0, 0.45)',
            }}
          />
        )}

        {hasMultipleImages && (
          <IconButton
            aria-label={t('common.actions.next')}
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: { xs: 12, sm: 24 },
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'common.white',
              bgcolor: 'rgba(0, 0, 0, 0.35)',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.55)' },
            }}
          >
            <ChevronRight />
          </IconButton>
        )}

        {hasImages && (
          <Typography
            variant="body2"
            sx={{
              position: 'absolute',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              px: 2,
              py: 0.75,
              borderRadius: 999,
              bgcolor: 'rgba(0, 0, 0, 0.45)',
              color: 'common.white',
            }}
          >
            {t('common.labels.imagePosition', { current: normalizedIndex + 1, total: images.length })}
          </Typography>
        )}
      </Box>
    </Dialog>
  );
};

export default FullScreenImageViewer;